import React, { useRef, useState } from 'react';
import { styled } from '@compiled/react';
import { useFragment, graphql } from 'react-relay';
import { Box, Text, xcss } from '@atlaskit/primitives';
import type { url_issueFieldUrlReadviewFull_UrlReadView$key as UrlFragment } from '@atlassian/jira-relay/src/__generated__/url_issueFieldUrlReadviewFull_UrlReadView.graphql';
import { EmptyFieldText } from '@atlassian/jira-issue-empty-field-text/src/ui/index.tsx';
import isValidEmailAddress from '@atlassian/jira-common-is-valid-email-address/src/index.tsx';
import prefixUrl from '@atlassian/jira-common-util-prefix-url/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { SimpleInlineSmartLink } from '@atlassian/jira-issue-field-smart-link/src/ui/simple-inline-smart-link/index.tsx';
import { HoverPopover } from '@atlassian/jira-hover-popover/src/ui/index.tsx';
import { mergeRefs } from '@atlassian/jira-merge-refs/src/index.tsx';
import { formatReadValue } from './utils.tsx';
import type { UrlReadViewProps } from './types.tsx';

// eslint-disable-next-line no-script-url
const forbiddenScheme = 'javascript:';

export const UrlReadView = ({
	fragmentRef,
	isSimpleInlineSmartLinkEnabled,
	shouldRenderHoverPopover,
}: UrlReadViewProps) => {
	const { uri: value } = useFragment<UrlFragment>(
		graphql`
			fragment url_issueFieldUrlReadviewFull_UrlReadView on JiraUrlField {
				uri
			}
		`,
		fragmentRef,
	);

	const [isUrlErrorState, setIsUrlErrorState] = useState(false);
	const contentRef = useRef(null);

	const handleSmartLinkError = () => {
		setIsUrlErrorState(true);
	};

	const handleHoverOut = () => {
		setIsUrlErrorState(false);
	};

	if (value == null || value === '') {
		return <EmptyFieldText />;
	}

	if (isSimpleInlineSmartLinkEnabled && fg('jsc_inline_editing_field_refactor')) {
		if (value.startsWith(forbiddenScheme)) {
			return shouldRenderHoverPopover ? (
				<HoverPopover content={value} contentRef={contentRef} onHoverOut={handleHoverOut}>
					{({ ref }) => (
						<Text maxLines={1} ref={mergeRefs(ref, contentRef)}>
							{value}
						</Text>
					)}
				</HoverPopover>
			) : (
				<Box xcss={truncatedStyles} ref={contentRef}>
					{value}
				</Box>
			);
		}
		let renderContent = (ref: React.Ref<HTMLAnchorElement> | undefined) => (
			<SimpleInlineSmartLink
				anchorTarget="_blank"
				url={prefixUrl(value, '//')}
				anchorRef={ref}
				onError={shouldRenderHoverPopover ? handleSmartLinkError : undefined}
			/>
		);
		if (isValidEmailAddress(value)) {
			renderContent = (ref: React.Ref<HTMLAnchorElement> | undefined) => (
				<SimpleInlineSmartLink
					anchorTarget="_blank"
					url={`mailto:${value}`}
					text={value}
					anchorRef={ref}
					onError={shouldRenderHoverPopover ? handleSmartLinkError : undefined}
				/>
			);
		}

		if (shouldRenderHoverPopover && isUrlErrorState) {
			return (
				<HoverPopover content={value} contentRef={contentRef} onHoverOut={handleHoverOut}>
					{({ ref }) => renderContent(mergeRefs(ref, contentRef))}
				</HoverPopover>
			);
		}

		return renderContent(undefined);
	}

	const lDisplayValue = formatReadValue(value);

	return <Ellipsis title={value}>{lDisplayValue}</Ellipsis>;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Ellipsis = styled.div({
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});

const truncatedStyles = xcss({
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});
